/**
 * Data layer for the obradors (tenants) feature.
 *
 * Reads go straight through `insforge.database.from('tenants')` (the console may
 * read the platform-managed tenants table — NOT any tenant-scoped operational
 * table). Every mutation goes through an edge function (`tenant-create`,
 * `tenant-update`) so the server enforces super-admin auth, deploys the default
 * structure, and writes the platform_audit_log.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type InfiniteData,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { Tenant } from '@obrava/shared';
import { useAuthorized } from '../../auth/AuthProvider.js';
import { insforge } from '../../lib/insforge.js';
import { queryKeys } from '../../lib/queryKeys.js';
import {
  tenantCreate,
  tenantUpdate,
  tenantOwnerGet,
  tenantOwnerSendReset,
  tenantOwnerCreate,
  tenantAuditList,
  type TenantCreateRequest,
  type TenantUpdateRequest,
  type AuditPage,
} from '../../lib/edge.js';
import type {
  OwnerInfo,
  OwnerResetResponse,
  OwnerCreateResponse,
} from '../../lib/types.js';

/**
 * The console list/detail row. Extends the shared Tenant read shape with the
 * optional `last_access_at` column shown in the P-01 list. The column does NOT
 * exist in the Onada 1 schema (nothing writes a tenant's last access yet), so it
 * is never projected and stays undefined — the UI renders "never". Add it to
 * TENANT_COLUMNS once a migration introduces the column and a writer populates it.
 */
export interface TenantRow extends Tenant {
  readonly last_access_at?: string | null;
}

/** Lightweight column projection for list + detail (no settings_json blob). */
const TENANT_COLUMNS =
  'id, name, legal_name, tax_id, sector, plan, language, status, admin_email, ' +
  'suspended_at, created_at, updated_at, deleted_at, deleted_by, deletion_reason';

/**
 * Fail-closed guard for direct PostgREST reads. With a dead session the SDK
 * silently falls back to the ANON key, and RLS then returns an EMPTY result
 * with HTTP 200 — indistinguishable from "no data". Whenever a read comes back
 * empty, confirm the session is still alive; if it is not, fail the query so
 * the UI shows the load error (and the AuthProvider watchdog bounces to
 * /login) instead of rendering a bogus empty directory. `getCurrentUser()`
 * also refreshes the access token through the auth cookie when it can, so a
 * recoverable session heals on the query retry.
 */
async function assertSessionAlive(): Promise<void> {
  const { data } = await insforge.auth.getCurrentUser();
  if (!data.user) {
    throw new Error('session expired');
  }
}

/**
 * Fetch all tenants (active + suspended + deactivated). Client-side filtering
 * keeps the "mostrar inactius" toggle and search instant; the data set is small
 * (one row per obrador) so a single read is appropriate.
 */
async function fetchTenants(): Promise<readonly TenantRow[]> {
  const { data, error } = await insforge.database
    .from('tenants')
    .select(TENANT_COLUMNS)
    .order('created_at', { ascending: false });
  if (error) {
    throw new Error(error.message);
  }
  const rows = (data ?? []) as unknown as TenantRow[];
  // An empty directory is almost certainly a lost session, not zero obradors.
  if (rows.length === 0) await assertSessionAlive();
  return rows;
}

async function fetchTenant(id: string): Promise<TenantRow | null> {
  const { data, error } = await insforge.database
    .from('tenants')
    .select(TENANT_COLUMNS)
    .eq('id', id)
    .maybeSingle();
  if (error) {
    throw new Error(error.message);
  }
  const row = (data as unknown as TenantRow | null) ?? null;
  // A missing row can also mean the anon fallback (RLS hides everything).
  if (row === null) await assertSessionAlive();
  return row;
}

/**
 * The reads below are gated on {@link useAuthorized} rather than
 * trusting that only the AuthGate can mount their screens. On a COLD LOAD the
 * list screen did fire this read while signed out (observed on the deployed
 * console: `records/tenants` → `200 []` between two failed token refreshes),
 * because gating what RENDERS does not gate what a hook FETCHES during the boot
 * render. `enabled` closes that door at the source: with no live session the
 * request never leaves the browser, so there is no anon-key read to mistake for
 * an empty directory. `assertSessionAlive` still covers the other case — a
 * session that dies while the console is open and already showing data.
 */
export function useTenants(): UseQueryResult<readonly TenantRow[], Error> {
  const authorized = useAuthorized();
  return useQuery({
    queryKey: queryKeys.tenants,
    queryFn: fetchTenants,
    enabled: authorized,
  });
}

export function useTenant(id: string): UseQueryResult<TenantRow | null, Error> {
  const authorized = useAuthorized();
  return useQuery({
    queryKey: queryKeys.tenant(id),
    queryFn: () => fetchTenant(id),
    enabled: authorized && id !== '',
  });
}

/**
 * The obrador owner (tenant admin) identity + account status.
 *
 * Gated like the reads above even though this one goes through an edge function
 * that checks the session server-side: signed out it would answer 401, not a
 * misleading empty, so the gate is about not firing a doomed request (and its
 * error state) from a screen that is on its way to /login.
 */
export function useTenantOwner(id: string): UseQueryResult<OwnerInfo, Error> {
  const authorized = useAuthorized();
  return useQuery({
    queryKey: queryKeys.tenantOwner(id),
    queryFn: async (): Promise<OwnerInfo> => {
      const { data, errorMessage } = await tenantOwnerGet(id);
      if (errorMessage || !data) {
        throw new Error(errorMessage ?? 'tenant-owner get failed');
      }
      return data.owner;
    },
    enabled: authorized && id !== '',
  });
}

/** Trigger a password-reset / invite code to the owner's email (audited). */
export function useSendOwnerReset(): UseMutationResult<OwnerResetResponse, Error, string> {
  return useMutation({
    mutationFn: async (tenantId: string): Promise<OwnerResetResponse> => {
      const { data, errorMessage } = await tenantOwnerSendReset(tenantId);
      if (errorMessage || !data) {
        throw new Error(errorMessage ?? 'tenant-owner send_reset failed');
      }
      return data;
    },
  });
}

export interface CreateOwnerArgs {
  readonly tenantId: string;
  readonly email: string;
  readonly fullName: string;
  /** When set, the owner's password is created directly; otherwise emailed. */
  readonly password?: string;
}

/** Provision the owner account directly (set password now or email a code). Audited. */
export function useCreateOwner(): UseMutationResult<OwnerCreateResponse, Error, CreateOwnerArgs> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: CreateOwnerArgs): Promise<OwnerCreateResponse> => {
      const { data, errorMessage } = await tenantOwnerCreate(args.tenantId, {
        email: args.email,
        fullName: args.fullName,
        ...(args.password ? { password: args.password } : {}),
      });
      if (errorMessage || !data) {
        throw new Error(errorMessage ?? 'tenant-owner create failed');
      }
      return data;
    },
    onSuccess: (_data, args) => {
      void qc.invalidateQueries({ queryKey: queryKeys.tenantOwner(args.tenantId) });
    },
  });
}

export function useCreateTenant(): UseMutationResult<Tenant, Error, TenantCreateRequest> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: TenantCreateRequest): Promise<Tenant> => {
      const { data, errorMessage, errorCode } = await tenantCreate(req);
      if (errorCode || errorMessage || !data) {
        // Prefer the stable code so the screen can map it to a precise message
        // (e.g. 'tax_id_taken'); fall back to the raw text, then a default.
        throw new Error(errorCode ?? errorMessage ?? 'tenant-create failed');
      }
      return data.tenant;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.tenants });
    },
  });
}

// ── Tenant activity log (F0.9) ────────────────────────────────────────────────

const AUDIT_PAGE_SIZE = 50;

export interface AuditFilters {
  readonly action: string | null;
  readonly entityType: string | null;
}

/** One page of the activity log plus the offset it was fetched at (for paging). */
interface AuditPageResult extends AuditPage {
  readonly offset: number;
}

/**
 * Infinite reader of a tenant's activity log through the tenant-audit-log edge
 * function. Offset-based paging matters: the server logs the (client-visible)
 * support access only on the FIRST page (offset 0), so "load more" (offset > 0)
 * does not spam the tenant's log.
 */
export function useTenantAudit(
  tenantId: string,
  filters: AuditFilters,
): UseInfiniteQueryResult<InfiniteData<AuditPageResult>, Error> {
  const authorized = useAuthorized();
  return useInfiniteQuery({
    queryKey: queryKeys.tenantAudit(tenantId, filters),
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<AuditPageResult> => {
      const { data, errorMessage } = await tenantAuditList(tenantId, {
        limit: AUDIT_PAGE_SIZE,
        offset: pageParam,
        ...(filters.action ? { action: filters.action } : {}),
        ...(filters.entityType ? { entityType: filters.entityType } : {}),
      });
      if (errorMessage || !data) {
        throw new Error(errorMessage ?? 'tenant-audit-log failed');
      }
      return { ...data, offset: pageParam };
    },
    getNextPageParam: (last) =>
      last.hasMore ? last.offset + last.entries.length : undefined,
    // Extra weight here: the FIRST page writes a support-access entry into the
    // tenant's own activity log, so an ungated fetch would stamp "Obrava looked
    // at your data" on a load that never even had a session.
    enabled: authorized && tenantId !== '',
  });
}

export function useUpdateTenant(): UseMutationResult<Tenant, Error, TenantUpdateRequest> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: TenantUpdateRequest): Promise<Tenant> => {
      const { data, errorMessage } = await tenantUpdate(req);
      if (errorMessage || !data) {
        throw new Error(errorMessage ?? 'tenant-update failed');
      }
      return data.tenant;
    },
    onSuccess: (tenant) => {
      void qc.invalidateQueries({ queryKey: queryKeys.tenants });
      void qc.invalidateQueries({ queryKey: queryKeys.tenant(tenant.id) });
    },
  });
}
